import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Paper,
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  TextField,
  InputAdornment,
  IconButton,
  Typography,
  Box,
  Button,
  Pagination,
  CircularProgress
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import dayjs from 'dayjs';
import axiosInstance from '../../lib/axios';
import CancelPresentationIcon from '@mui/icons-material/CancelPresentation';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const BASE_COLUMNS = [
  { id: 'WingName', label: 'Wing', minWidth: 40, maxWidth: 40, width: 40 },
  { id: 'FloorName', label: 'Floor', minWidth: 40, maxWidth: 40, width: 40 },
  { id: 'RoomTypeName', label: 'Type', minWidth: 60, maxWidth: 60, width: 60 },
  { id: 'RoomViewNAme', label: 'View', minWidth: 40, maxWidth: 40, width: 40 },
  { id: 'RoomBedTypeName', label: 'BType', minWidth: 40, maxWidth: 40, width: 40 },
  { id: 'RoombedName', label: 'BedNo', minWidth: 30, maxWidth: 30, width: 30 },
  { id: 'BedRent', label: 'Rent', minWidth: 30, maxWidth: 30, width: 30 },
];

const generateColumns = (startDate, endDate) => {
  const baseColumnWidths = {
    WingName: 80,
    FloorName: 80,
    BedRent: 80,
    RoomNo: 80,
    RoombedName: 80,
    RoomTypeName: 80,
    RoomBedTypeName: 80,
    RoomViewNAme: 80
  };

  const baseColumns = BASE_COLUMNS.map(col => ({
    ...col,
    minWidth: baseColumnWidths[col.id],
    maxWidth: baseColumnWidths[col.id],
    width: baseColumnWidths[col.id]
  }));

  const columns = [];
  let currentDate = dayjs(startDate).startOf('month');
  const endMonth = dayjs(endDate).endOf('month');

  while (currentDate.isBefore(endMonth) || currentDate.isSame(endMonth, 'month')) {
    const year = currentDate.year();
    const month = MONTHS[currentDate.month()];
    if (currentDate.month() === 0 || currentDate.isSame(startDate, 'month')) {
      columns.push({
        id: `year_${year}`,
        label: 'Year',
        minWidth: 60,
        maxWidth: 60,
        width: 60
      });
    }

    columns.push({
      id: `${year}_${month}`,
      label: month.substring(0, 3),
      minWidth: 30,
      maxWidth: 30,
      width: 30
    });

    currentDate = currentDate.add(1, 'month');
  }
  columns.push({
    id: `action`,
    label: 'Action',
    minWidth: 60,
    maxWidth: 60,
    width: 60
  });
  return [...baseColumns, ...columns];
};

const transformData = (apiData, startDate, endDate) => {
  const roomDataMap = new Map();

  apiData.forEach(item => {
    if (!roomDataMap.has(item.RoomBedId)) {
      roomDataMap.set(item.RoomBedId, {
        RoomNo: item.RoomNo,
        RoombedName: item.RoombedName,
        RoomTypeName: item.RoomTypeName,
        RoomBedTypeName: item.RoomBedTypeName,
        RoomViewNAme: item.RoomViewNAme,
        WingName: item.WingName,
        FloorName: item.FloorName,
        BedRent: item.BedRent,
        WingId: item.WingId,
        FloorId: item.FloorId,
        RoomTypeId: item.RoomTypeId,
        RoomBedId: item.RoomBedId,
        RoomBedTypeId: item.RoomBedTypeId,
        RoomViewId: item.RoomViewId,
        RoomId: item.RoomId,
        BedCatId: item.BedCatId,
        RoomBedId:item.RoomBedId,
        monthData: new Map()
      });
    }
    const roomData = roomDataMap.get(item.RoomBedId);
    const monthKey = `${item.YearNm}_${item.MonthNm}`;
    roomData.monthData.set(monthKey, item.BedStatus);
  });

  const transformedData = [];
  roomDataMap.forEach((roomData, roomBedId) => {
    const rowData = {
      RoomNo: roomData.RoomNo,
      RoombedName: roomData.RoombedName,
      RoomTypeName: roomData.RoomTypeName,
      RoomBedTypeName: roomData.RoomBedTypeName,
      RoomViewNAme: roomData.RoomViewNAme,
      WingName: roomData.WingName,
      FloorName: roomData.FloorName,
      BedRent: roomData.BedRent,
      WingId: roomData.WingId,
      FloorId: roomData.FloorId,
      RoomTypeId: roomData.RoomTypeId,
      RoomBedId: roomData.RoomBedId,
      RoomBedTypeId: roomData.RoomBedTypeId,
      RoomViewId: roomData.RoomViewId,
      RoomId: roomData.RoomId,
       BedCatId: roomData.BedCatId,
       RoomBedId:roomData.RoomBedId
    };

    let currentDate = dayjs(startDate).startOf('month');
    const endMonth = dayjs(endDate).endOf('month');

    while (currentDate.isBefore(endMonth) || currentDate.isSame(endMonth, 'month')) {
      const year = currentDate.year();
      const month = MONTHS[currentDate.month()];

      if (currentDate.month() === 0 || currentDate.isSame(startDate, 'month')) {
        rowData[`year_${year}`] = year;
      }

      const monthKey = `${year}_${month}`;
      const bedStatus = roomData.monthData.get(monthKey);
      rowData[monthKey] = bedStatus || '';

      currentDate = currentDate.add(1, 'month');
    }

    transformedData.push(rowData);
  });

  return transformedData;
};

const getMonthStyle = (value) => {
  switch (value) {
    case "V":
      return { backgroundColor: "green", color: "white" };
    case "B":
      return { backgroundColor: "#635bff", color: "white" };
    case "O":
      return { backgroundColor: "yellow", color: "black" };
    case "A":
      return { backgroundColor: "#046A38", color: "white" };
    case "C":
      return { backgroundColor: "#c971bb", color: "white" };
    case "R":
      return { backgroundColor: "#47a1ba", color: "white" };
    case "M":
      return { backgroundColor: "#800000", color: "white" };
    case "P":
      return { backgroundColor: "#FF671F", color: "white" };
    case "L":
      return { backgroundColor: "#FF671F", color: "white" };
    default:
      return {};
  }
};

const RoomSelection = ({ 
  open, 
  onClose, 
  propId, 
  dateFrom, 
  dateTo,
  onRoomSelect,
  defaultSearchValue,
  BedCatId
}) => {
  const [rows, setRows] = useState([]);
  const [tableColumns, setTableColumns] = useState(BASE_COLUMNS);
  const [searchText, setSearchText] = useState(defaultSearchValue || '');
  const [searchTerms, setSearchTerms] = useState({});
  const [loader, setLoader] = useState(false);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 180,
    totalRecords: 0,
    totalPages: 0
  });

  const fetchBedCalendar = async (page = 1, pageSize = 10000, searchText = "") => {
    try {
      setLoader(true);

      const queryParams = new URLSearchParams({
        currentPage: page,
        limit: 10000,
        PageSize: 10000,
        PageNumber: page
      });

      const payload = {
        PropId: propId,
        BookDtFrom: dateFrom,
        CheckoutDt: dateTo,
        Flag: "",
        start: page,
        PageSize: 10000,
        SearchText: searchText,
        BedCatId:BedCatId
      };
      await axiosInstance.post(
        `BedCalendar/InsertBedCat`,
        { PropId: propId, Flag: "" }
      );

      await axiosInstance.post(
        `BedCalendar/InsertBedCatCal`,
        { PropId: propId, Flag: "" }
      );
      await axiosInstance.post(
        `BedCalendar/UpdateBedCat`,
        { PropId: propId, Flag: "" }
      );

      await axiosInstance.post(
        `BedCalendar/UpdateBedCatCal`,
        { PropId: propId, Flag: "" }
      );
      const response = await axiosInstance({
        method: 'post',
        url: `BedCalendar/GetPropWiseBedCalendarMonth?${queryParams.toString()}`,
        data: payload
      });

      if (response?.data?.Status === 0) {
        const newColumns = generateColumns(dateFrom, dateTo);
        setTableColumns(newColumns);
        const transformedData = transformData(response.data.Data, dateFrom, dateTo);
        setRows(transformedData);
        
        const totalRecords = response.data.TotalCount || response.data.PageData?.TotalRecords || 0;
        const calculatedPageSize = response.data.PageData?.PageSize || pageSize;
        
        setPagination(prev => ({
          ...prev,
          totalRecords: totalRecords,
          currentPage: page,
          totalPages: Math.ceil(totalRecords / calculatedPageSize),
          pageSize: calculatedPageSize
        }));
      } else {
        console.error(response?.data?.Message || "Error fetching data");
        setRows([]);
      }
    } catch (error) {
      console.error('Error fetching calendar data:', error);
    } finally {
      setLoader(false);
    }
  };

  const handlePageChange = (newPage) => {
    fetchBedCalendar(
      newPage,
      pagination.pageSize,
      searchText
    );
  };

  const handleRowsPerPageChange = (newPageSize) => {
    setPagination(prev => ({
      ...prev,
      pageSize: newPageSize,
      currentPage: 1
    }));
    fetchBedCalendar(
      1,
      newPageSize,
      searchText
    );
  };

  const handleSearchChangeGlobal = (e) => {
    const value = e.target.value;
    setSearchText(value);
    const debounceTimer = setTimeout(() => {
      if (value.length >= 3 || value.length === 0) {
        fetchBedCalendar(
          1,
          pagination.pageSize,
          value
        );
      }
    }, 500);

    return () => clearTimeout(debounceTimer);
  };

  const handleViewBedClick = (row) => {
    const params = new URLSearchParams({
      bedId: row.RoomBedId,
      fromDate: dateFrom,
      toDate: dateTo,
      propId: propId
    });
    window.open(`/bed-details?${params.toString()}`, '_blank');
  };

  // useEffect(() => {
  //   if (open) {
  //     fetchBedCalendar();
  //   }
  // }, [open, dateFrom, dateTo, propId]);

//   useEffect(() => {
//   if (open) {
//     fetchBedCalendar(1, pagination.pageSize, defaultSearchValue || '');
//     setSearchText(defaultSearchValue || '');
//   }
// }, [open, dateFrom, dateTo, propId]);
useEffect(() => {
  if (open) {
    setSearchText(defaultSearchValue || '');
    fetchBedCalendar(1, pagination.pageSize, defaultSearchValue || '');
  }
}, [defaultSearchValue, open]);

const filterRows = (data, searchTerms) => {
  if (Object.keys(searchTerms).length === 0) return data;
  
  return data.filter(row => {
    return Object.entries(searchTerms).every(([columnId, searchValue]) => {
      const cellValue = String(row[columnId] || '').toLowerCase();
      return cellValue.includes(searchValue.toLowerCase());
    });
  });
};

const filteredRows = filterRows(rows, searchTerms);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      sx={{
        '& .MuiDialog-container': {
          alignItems: 'flex-start'
        }
      }}
    >
      <DialogTitle style={{
        padding: '30px 20px',
        height: '40px',
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        position: 'sticky',
        top: 0,
        backgroundColor: 'white',
        zIndex: 1,
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <TextField
            placeholder="Search across all columns..."
            variant="outlined"
            size="small"
            value={searchText}
            onChange={handleSearchChangeGlobal}
            sx={{
              width: 300,
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                backgroundColor: '#f9f9f9',
              },
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchText && (
                <IconButton
                  size="small"
                  onClick={() => {
                    setSearchText('');
                    handleSearchChangeGlobal({ target: { value: '' } });
                  }}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              )
            }}
            helperText={searchText.length > 0 && searchText.length < 3 ?
              "Type at least 3 characters to search" : ""}
          />
          <Typography variant="h6">Room Selection</Typography>
        </Box>

        <Button
          variant="contained"
          size="small"
          sx={{
            background: '#635bff',
            height: '35px',
            color: 'white',
            borderRadius: '5px',
          }}
          onClick={onClose}
        >
          <CancelPresentationIcon />
        </Button>
      </DialogTitle>

      <DialogContent sx={{
        padding: 0,
        height: 'calc(100vh - 120px)',
        overflow: 'hidden'
      }}>
        {loader ? (
          <Box sx={{
            display: 'flex',
            justifyContent: "center",
            alignItems: "center",
            height: '100%'
          }}>
            <CircularProgress />
          </Box>
        ) : (
          <Paper sx={{
            width: "100%",
            height: "100%",
            overflow: "hidden",
            margin: "0px auto",
            backgroundColor: "#ffffff",
            borderRadius: 0,
            boxShadow: "none",
            display: 'flex',
            flexDirection: 'column'
          }}>
            <TableContainer sx={{
              flex: 1,
              overflow: 'auto',
              '&::-webkit-scrollbar': {
                width: '8px',
                height: '8px'
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: '#635bff',
                borderRadius: '4px'
              }
            }}>
              <Table stickyHeader aria-label="sticky table">
                <TableHead>
                  <TableRow sx={{
                    '& > th': {
                      padding: '2px 10px',
                      textAlign: 'center',
                      position: 'sticky',
                      top: 0,
                      zIndex: 2,
                      backgroundColor: '#dddbff'
                    }
                  }}>
                    {tableColumns.map((column) => (
                      <TableCell
                        key={column.id}
                        align={"center"}
                        sx={{
                          minWidth: column.minWidth,
                          width: column.minWidth,
                          backgroundColor: "#dddbff"
                        }}
                      >
                        <Typography variant="subtitle1" fontWeight="bold">
                          {column.label}
                        </Typography>
                        <TextField
                          size="small"
                          variant="outlined"
                          placeholder={`Search ${column.label}`}
                          value={searchTerms[column.id] || ''}
                          onChange={(e) => {
                            const newSearchTerms = { ...searchTerms };
                            if (e.target.value) {
                              newSearchTerms[column.id] = e.target.value;
                            } else {
                              delete newSearchTerms[column.id];
                            }
                            setSearchTerms(newSearchTerms);
                          }}
                          sx={{
                            mt: 1,
                            margin: '0px',
                            '& .MuiOutlinedInput-input': {
                              padding: '2px 2px',
                            },
                            backgroundColor: "#ffffff",
                            borderRadius: 1
                          }}
                         
                        />
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {filteredRows.map((row, index) => (
                    <TableRow
                      hover
                      role="checkbox"
                      tabIndex={-1}
                      key={index}
                      // onDoubleClick={() => onRoomSelect(row)}
                      onDoubleClick={() => {
  console.log('Double-clicked row:', row); // ✅ Check if WingName, etc. exist here
  onRoomSelect(row);
}}

                      style={{ cursor: 'pointer', height: '2px' }}
                      sx={{
                        "& > td": {
                          padding: "1px",
                          border: "0.5px solid #ddd",
                        },
                        "&:hover": {
                          backgroundColor: "#f1f1f1",
                        },
                      }}
                    >
                      {tableColumns.map((column) => {
                        const value = row[column.id];
                        return (
                          <TableCell
                            key={column.id}
                            align={"center"}
                            sx={column.id !== "WingName" && getMonthStyle(value)}
                          >
                            {column.id === 'action' ? (
                              <Button
                                variant="contained"
                                size="small"
                                color="primary"
                                sx={{
                                  padding: '2px 6px',
                                  fontSize: '0.75rem',
                                  minWidth: '80px',
                                  textTransform: 'none',
                                  backgroundColor: '#635bff',
                                  '&:hover': { backgroundColor: '#635bff' },
                                }}
                                onClick={() => handleViewBedClick(row)}
                              >
                                View Bed
                              </Button>
                            ) : (
                              column.id === "FloorName" ? value?.split(" ")[0] : value
                            )}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              sx={{
                backgroundColor: "#dddbff",
                position: 'sticky',
                bottom: 0,
                borderTop: '1px solid #ddd',
                '& .MuiTablePagination-toolbar': {
                  display: 'flex',
                  justifyContent: 'flex-end',
                  alignItems: 'center',
                  padding: '0 6px',
                  minHeight: '52px',
                  gap: '2px'
                },
                '& .MuiTablePagination-spacer': {
                  display: 'none'
                },
                '& .MuiTablePagination-selectLabel': {
                  margin: 0
                },
                '& .MuiTablePagination-displayedRows': {
                  margin: 0
                },
                '& .MuiTablePagination-actions': {
                  marginLeft: 0
                }
              }}
              rowsPerPageOptions={[50, 100, 150, 250, 500, 1000, 2500, 5000]}
              component="div"
              count={pagination.totalRecords}
              rowsPerPage={pagination.pageSize}
              page={pagination.currentPage - 1}
              onPageChange={(event, newPage) => {
                handlePageChange(newPage + 1);
              }}
              onRowsPerPageChange={(event) => {
                handleRowsPerPageChange(parseInt(event.target.value, 10));
              }}
              ActionsComponent={() => (
                <>
                  <Typography
                    variant="body2"
                    sx={{ position: 'absolute', left: '20px', fontWeight: 'bold', color: "#333", }}
                  >
                    Total Records: {pagination.totalRecords}
                  </Typography>
                  <Pagination
                    count={pagination.totalPages}
                    page={pagination.currentPage}
                    onChange={(event, newPage) => handlePageChange(newPage)}
                    showFirstButton
                    showLastButton
                    sx={{
                      "& .MuiPaginationItem-root.Mui-selected": {
                        backgroundColor: "#635bff",
                        color: "#ffffff",
                      },
                      '& .MuiPagination-ul': {
                        flexWrap: 'nowrap',
                        margin: 0
                      }
                    }}

                  />
                </>
              )}
            />
          </Paper>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default RoomSelection;